import { useContext, useEffect, useRef, useState } from 'react'
import { Configuration, ConfiguratorContext } from '../../Contexts/Configuration'
import PlanLayout from '../../Layouts/PlanLayout/PlanLayout'
import Plan from '../../Models/Plan'
import API from '../../Services/API'
import { NavigationNode } from '../../Services/Navigation'
import ViewType from '../../Types/ViewType'
import './DocumentsUploadView.css'
import CustomerRequest from '../../Requests/CustomerRequest'
import { CustomerType } from '../../Enums/CustomerType'
import DatePicker from 'react-date-picker'
import { DocumentType } from '../../Enums/DocumentType'
import Radio from '../../../../Components/Radio/Radio'
import { validateDrivingLicense, validateIdentityCard } from '../../Services/Validation'

const DocumentsUploadView = () => {
	let context = useContext(ConfiguratorContext)

	let [plan, setPlan] = useState<Plan | undefined>(undefined)
	let [file1, setFile1] = useState<Blob | undefined>(undefined)
	let [file2, setFile2] = useState<Blob | undefined>(undefined)
	let [file3, setFile3] = useState<Blob | undefined>(undefined)
	let [file4, setFile4] = useState<Blob | undefined>(undefined)
	let [singleFile, setSingleFile] = useState<boolean>(true)
	let [FileError, setFileError] = useState("")

	let [documentType, setDocumentType] = useState(context.documentType)
	let [documentNumber, setDocumentNumber] = useState(context.documentNumber)
	let [documentNumberError, setDocumentNumberError] = useState("")
	let [documentExpiration, setDocumentExpiration] = useState(context.documentExpiration)
	let [documentExpirationError, setDocumentExpirationError] = useState("")

	let [uploadError, setUploadError] = useState<Boolean>(false)

	const input1 = useRef<HTMLInputElement | null>(null)
	const input2 = useRef<HTMLInputElement | null>(null)
	const input3 = useRef<HTMLInputElement | null>(null)
	const input4 = useRef<HTMLInputElement | null>(null)

	context.validateDocumentsView = async () => {
		let validDocumentNumber = false
		if (documentType === DocumentType.IdentityCard) {
			if (documentNumber !== null) validDocumentNumber = validateIdentityCard(documentNumber?.replaceAll(" ", ""))
			else validDocumentNumber = false
		} else {
			if (documentNumber !== null) validDocumentNumber = validateDrivingLicense(documentNumber?.replaceAll(" ", ""))
			else validDocumentNumber = false
		}
		context.documentType = documentType

		if (!validDocumentNumber) setDocumentNumberError("Il numero del documento inserito non è valido")
		else setDocumentNumberError("")
		context.documentNumber = documentNumber

		let validDocumentExpiration = !(documentExpiration === undefined || documentExpiration < new Date())
		if (!validDocumentExpiration) setDocumentExpirationError("Inserire una data di scadenza valida")
		else setDocumentExpirationError("")
		context.documentExpiration = documentExpiration

		if (!(validDocumentNumber && validDocumentExpiration)) {
			return false
		}

		let documentCheckPassed = false
		let checkRes = await documentCheck()
		if (!checkRes) {
			setDocumentNumberError("Documento già usato da un altro utente")
			documentCheckPassed = false
		} else {
			setDocumentNumberError("")
			documentCheckPassed = true
		}

		if (!(documentCheckPassed)) {
			return false
		}

		if (singleFile) {
			if (file1 === undefined || file2 === undefined) {
				setFileError("Si prega di caricare tutti i file richiesti")
				return false
			}

			encodeAndUploadFile(file1, 'document')
			encodeAndUploadFile(file2, 'tax_id')

			if (uploadError === true) {
				setFileError("Errore durante il caricamento, si prega di riprovare")
				return false
			}
		} else {

			if (file1 === undefined || file2 === undefined || file3 === undefined || file4 === undefined) {
				setFileError("Si prega di caricare tutti i file richiesti")
				return false
			}

			encodeAndUploadFile(file1, 'document_front')
			encodeAndUploadFile(file2, 'document_back')
			encodeAndUploadFile(file3, 'tax_id_front')
			encodeAndUploadFile(file4, 'tax_id_back')

			if (uploadError === true) {
				setFileError("Errore durante il caricamento, si prega di riprovare")
				return false
			}
		}

		return true
	}

	var selectionChanged = context.options?.flatMap(x => [...x.selection].sort((a, b) => a[0] - b[0]).map(x => x[1])).join("")

	useEffect(() => {
		(async () => {
			let m = []
			for (var d of context.options ?? []) {
				m.push({ id: d.parentId, products: [...d.selection].filter(x => x[1]).map(x => x[0]) })
			}
			let p = await API.getPlanAsync(context.selectedOffer!.id, {
				number_portability: context.keepNumber,
				wifi_consultancy: context.wifiCoverageCheck,
				payment_type: context.paymentType !== undefined ? context.paymentType.name : '',
				payment_mode: context.paymentMode !== undefined ? context.paymentMode.name : '',
				customer_type_id: context.isHomeUse ? 1 : 2,
				data: m
			})
			if (p.success) {
				setPlan(p.data)
			}

			let customer: CustomerRequest = {
				business_name: context.businessName,
				first_name: context.name!,
				last_name: context.surname!,
				email: context.email!,
				pec: context.pec,
				status: "prospect",
				phone: context.phone,
				address: context.address,
				billing_address: context.billingAddress,
				cap: context.cap,
				billing_cap: context.billingCap,
				province: context.province,
				billing_province: context.billingProvince,
				municipality: context.common,
				billing_municipality: context.billingMunicipality,
				city: context.common,
				birth_place: context.birthMunicipality === undefined ? undefined : context.birthMunicipality + ' ' + context.birthProvince,
				fiscal_code: context.fiscalCode,
				vat_number: context.vatNumber,
				sdi: context.sdi,
				gender: context.gender ? 'M' : 'F',
				birthday: context.birthDate,
				civic_number: parseInt(context.civicNumber!),
				billing_civic_number: parseInt(context.billingCivicNumber!),
				type: context.isHomeUse ? CustomerType.Private : CustomerType.Professional,
				iban: context.iban,
				bank: context.bank,
				bank_place: context.place,
			}
			await API.updateCustomerAsync(context.customerId!, customer)
		})()
		return () => { }
	}, [context.keepNumber, context.paymentType, context.wifiCoverageCheck, context.selectedOffer, context.options, selectionChanged])

	useEffect(() => {
		(async () => {
			setFile1(undefined)
			setFile2(undefined)
			setFile3(undefined)
			setFile4(undefined)
		})()
		return () => { }
	}, [singleFile])

	const onChangeFile = (event: any, set: any) => {
		event.stopPropagation();
		event.preventDefault();
		var file = event.target.files[0];
		set(file)
	}

	async function encodeAndUploadFile(file: Blob, fileName: string) {
		let reader = new FileReader()
		reader.onload = async () => {
			let res = await API.uploadDocumentAsync(context.customerId!, {
				encoded_file: reader.result,
				file_name: fileName,
			})
			setUploadError(res.success)
		}
		reader.readAsDataURL(file)
	};

	async function documentCheck() {
		let res = await API.checkDocumentAsync(
			{
				first_name: context.name!,
				last_name: context.surname!,
				email: context.email!,
				document_type: documentType!,
				document_number: documentNumber!
			}
		)
		if (res.success) {
			return res.data
		}
		return false
	}

	function GetFileUploads() {
		if (singleFile) {
			return (
				<div>
					<div className='document-page-file-container'>
						<input type="file" ref={input1} style={{ display: 'none' }} onChange={(e) => onChangeFile(e, setFile1)} />
						<div className='identity-card-title'>Documento</div>
						<div className='identity-card-button-container'>
							<button className='identity-card-button' onClick={() => input1.current?.click()}><b>SCEGLI FILE</b></button>
							{file1 !== undefined ? <div>Ok</div> : null}
						</div>
					</div>

					<div className='document-page-file-container'>
						<input type="file" ref={input2} style={{ display: 'none' }} onChange={(e) => onChangeFile(e, setFile2)} />
						<div className='identity-card-title'>Codice Fiscale</div>
						<div className='identity-card-button-container'>
							<button className='identity-card-button' onClick={() => input2.current?.click()}><b>SCEGLI FILE</b></button>
							{file2 !== undefined ? <div>Ok</div> : null}
						</div>
					</div>
				</div>
			)
		} else {
			return (
				<div>
					<div className='document-page-file-container'>
						<input type="file" ref={input1} style={{ display: 'none' }} onChange={(e) => onChangeFile(e, setFile1)} />
						<div className='identity-card-title'>Documento fronte</div>
						<div className='identity-card-button-container'>
							<button className='identity-card-button' onClick={() => input1.current?.click()}><b>SCEGLI FILE</b></button>
							{file1 !== undefined ? <div>Ok</div> : null}
						</div>
					</div>

					<div className='document-page-file-container'>
						<input type="file" ref={input2} style={{ display: 'none' }} onChange={(e) => onChangeFile(e, setFile2)} />
						<div className='identity-card-title'>Documento retro</div>
						<div className='identity-card-button-container'>
							<button className='identity-card-button' onClick={() => input2.current?.click()}><b>SCEGLI FILE</b></button>
							{file2 !== undefined ? <div>Ok</div> : null}
						</div>
					</div>

					<div className='document-page-file-container'>
						<input type="file" ref={input3} style={{ display: 'none' }} onChange={(e) => onChangeFile(e, setFile3)} />
						<div className='identity-card-title'>Codice fiscale fronte</div>
						<div className='identity-card-button-container'>
							<button className='identity-card-button' onClick={() => input3.current?.click()}><b>SCEGLI FILE</b></button>
							{file3 !== undefined ? <div>Ok</div> : null}
						</div>
					</div>

					<div className='document-page-file-container'>
						<input type="file" ref={input4} style={{ display: 'none' }} onChange={(e) => onChangeFile(e, setFile4)} />
						<div className='identity-card-title'>Codice fiscale retro</div>
						<div className='identity-card-button-container'>
							<button className='identity-card-button' onClick={() => input4.current?.click()}><b>SCEGLI FILE</b></button>
							{file4 !== undefined ? <div>Ok</div> : null}
						</div >
					</div>
				</div>
			)
		}
	}

	return (
		<PlanLayout plan={plan}>
			<div className='identity-card-container'>

				<div className='payment-type-view-title'>Caricamento Documenti</div>

				<div className='document-page-radio-container'>
					<div className='payment-type-radio'>
						<Radio key={1} checked={documentType !== undefined && documentType === DocumentType.IdentityCard} onChange={() => setDocumentType(DocumentType.IdentityCard)} />
						Carta di Identità
					</div>
					<div className='payment-type-radio'>
						<Radio key={2} checked={documentType !== undefined && documentType === DocumentType.DrivingLicense} onChange={() => setDocumentType(DocumentType.DrivingLicense)} />
						Patente di Guida
					</div>
				</div>

				<div className='document-page-question-wrapper'>
					<div className="document-page-label-question">Numero documento</div>
					<input onChange={b => setDocumentNumber(b.target.value.toUpperCase())} value={documentNumber} className="document-page-input" />
					<div className='label-error'>{documentNumberError}</div>
				</div>

				<div className='document-page-question-wrapper'>
					<div className="document-page-label-question">Scadenza documento</div>
					<DatePicker
						className="date-picker"
						calendarClassName="date-picker-calendar"
						tileClassName="date-picker-tile"
						onChange={(value: Date, _: any) => setDocumentExpiration(value)}
						value={documentExpiration}
					/>
					<div className='label-error'>{documentExpirationError}</div>
				</div>

				<div className='document-page-question-wrapper'>
					<div className="document-page-label-question">Come intendi caricare i documenti?</div>
					<div className="document-page-radio-container">
						<div className='payment-type-radio'>
							<Radio onChange={() => setSingleFile(true)} checked={singleFile} />
							File unico
						</div>
						<div className='payment-type-radio'>
							<Radio onChange={() => setSingleFile(false)} checked={!singleFile} />
							File fronte e retro
						</div>
					</div>
				</div>

				{GetFileUploads()}

				<div className='label-error'>{FileError}</div>
			</div>
		</PlanLayout>
	)
}

const IdentityCardNode: NavigationNode = {
	next: () => ViewType.lastPage,
	previous: (context: Configuration) => context.paymentType?.name.toLowerCase().includes('bonifico') ? ViewType.paymentType : ViewType.bankForm,
	content: () => <DocumentsUploadView />,
	percentage: 95
}

export default IdentityCardNode
